import { Badge, Box, Button, ButtonGroup, Text } from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { When } from "components/When/When";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { getFieldErrors } from "utils/react-hook-form";
import { formKey } from "./util/formKey";
import { useNestedFormData } from "../useNestedFormData";
import { Dispatch, SetStateAction } from "react";

interface Props {
  bundleGroupIndex: number;
  bundleIndex: number;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  onCancel: () => void;
  isNew: boolean;
}

const GridBox = styled(Box)`
  display: grid;
  gap: 8px;
  grid-auto-flow: row;
`;

const StyledBox = styled(Box)`
  margin-left: auto;
  align-self: end;
`;

export const ValidateBundleFormButton = ({
  bundleGroupIndex,
  bundleIndex,
  setShowModal,
  onCancel,
  isNew
}: Props) => {
  const {
    trigger,
    formState: { errors },
    setError
  } = useFormContext<ProgramService.ProgramModel>();
  const {
    bundleKey,
    bundle: { metaTemplates = [], deltaTemplates = [], customTemplates = [] }
  } = useNestedFormData({
    bundleGroupIndex,
    bundleIndex
  });

  const checkCustomValidation = () => {
    const allTemplates = [
      ...(metaTemplates ?? []),
      ...(deltaTemplates ?? []),

      // Budget allocation is irrelevant for templates invoiced by external party
      ...(customTemplates?.filter(x => !x.invoicedByExternalParty) ?? [])
    ];

    const totalBudgetAllocation = allTemplates.reduce(
      (acc, curr) => (acc += curr.percentageOfTotalBudget ?? 0),
      allTemplates.length === 0 ? 100 : 0
    );

    if (totalBudgetAllocation !== 100) {
      setError("root.budget", {
        type: "custom",
        message:
          "The sum of the budget allocation for all templates must be 100%."
      });

      return false;
    }

    return true;
  };

  const triggerPartialValidation = async () => {
    const bundleValid = trigger([
      `${bundleKey}.name`,
      `${bundleKey}.paidBy`,
      `${bundleKey}.paymentMethod`,
      `${bundleKey}.price.amount`,
      `${bundleKey}.totalBudget`,
      `${bundleKey}.maxNumberOfOrders`,
      `${bundleKey}.estimatedReach`
    ]);

    const metaValid = metaTemplates?.map(async (_, i) => {
      const [templateKey] = formKey(bundleGroupIndex, bundleIndex, i, "meta");
      return trigger([
        `${templateKey}.name`,
        `${templateKey}.percentageOfTotalBudget`,
        `${templateKey}.durationDays`,
        `${templateKey}.landingPageOption`,
        `${templateKey}.selectedImageTemplates`,
        `${templateKey}.selectedContentTemplates`,
        `${templateKey}.selectedAdType`,
        `${templateKey}.staticCoordinates.latitude`,
        `${templateKey}.staticCoordinates.longitude`
      ]);
    }) ?? [true];

    const deltaValid = deltaTemplates?.map(async (_, i) => {
      const [templateKey] = formKey(bundleGroupIndex, bundleIndex, i, "delta");
      return trigger([
        `${templateKey}.name`,
        `${templateKey}.percentageOfTotalBudget`,
        `${templateKey}.durationDays`,
        `${templateKey}.landingPageOption`,
        `${templateKey}.externalDeltaTemplateId`,
        `${templateKey}.staticCoordinates.latitude`,
        `${templateKey}.staticCoordinates.longitude`
      ]);
    }) ?? [true];

    const customValid = customTemplates?.map(async (_, i) => {
      const [templateKey] = formKey(bundleGroupIndex, bundleIndex, i, "custom");
      return trigger([
        `${templateKey}.name`,
        `${templateKey}.percentageOfTotalBudget`,
        `${templateKey}.durationDays`,
        `${templateKey}.channel`,
        `${templateKey}.estimatedExternalPartyPrice`
      ]);
    }) ?? [true];

    const campaignTemplateValidationStatuses = await Promise.all([
      bundleValid,
      ...metaValid,
      ...deltaValid,
      ...customValid
    ]);

    if (
      checkCustomValidation() &&
      campaignTemplateValidationStatuses.every(x => x)
    ) {
      setShowModal(false);
    }
  };

  const fieldErrors = getFieldErrors(errors);

  return (
    <Box display="flex" flexDirection="row">
      <When truthy={fieldErrors.length > 0}>
        <Box>
          <Text as="p" bold>
            Form errors
          </Text>

          <GridBox pY="xs">
            {fieldErrors.map(x => (
              <Badge color="warning" key={x}>
                {x}
              </Badge>
            ))}
          </GridBox>
        </Box>
      </When>

      <StyledBox display="flex" justifyContent="right">
        <ButtonGroup>
          <Button
            logLabel="cancel-bundle-editing-button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            logLabel="done-bundle-editing-button"
            variant="primary"
            onClick={triggerPartialValidation}
            data-testid="done-bundle-editing"
          >
            {isNew ? "Add" : "Edit"}
          </Button>
        </ButtonGroup>
      </StyledBox>
    </Box>
  );
};
